/**
 * GrupoTemplate — template CRUD da entidade Grupo.
 *
 * Grupos definem perfis de acesso (ex: Super Administrador, Administrador, Leitura).
 * O flag `acessototal` concede bypass de permissionamento por ação (TokenService.js) - não
 * confundir com escopo de dados entre tenants (Publicacao.permiteacessogeral). `acessoinicial`
 * define se novas permissões sincronizadas são permitidas por padrão para o grupo.
 */
import React, { Component } from 'react';
import { confirmDialog } from 'primereact/confirmdialog';

import { EntityGrid } from 'hay-core-react';
import { ModelTextInput } from 'hay-core-react';
import { ModelBoolean } from 'hay-core-react';
import { UtilService } from 'hay-core-react';
import Grupo from '../../../models/Grupo';
import { buildRelacionados } from '../../../service/RotasService';

// ─── Esquema de campos ────────────────────────────────────────────────────────

// acessototal concede bypass de permissionamento por ação (TokenService.js) - só quem acessou por
// uma publicação de acesso geral (permissoes.permiteacessogeral) pode ver/editar esse campo em
// outros grupos, senão qualquer admin de tenant conseguiria se auto-conceder o bypass
// (BaseController.js trava o campo no backend pela mesma checagem, ver especificações 185/186;
// isso aqui é só a UI acompanhando).
function buildModel(mostrarAcessoTotal) {
    return {
        nome: {
            exibir: 'main.grupo.nome',
            visivel: true,
            validacao: { obrigatorio: true }
        },
        descricao: {
            exibir: 'main.grupo.descricao',
            visivel: true,
        },
        ...(mostrarAcessoTotal ? {
            acessototal: {
                exibir: 'main.grupo.acessototal',
                visivel: true,
                tipo: 'boolean',
                template: 'acessototal.template',
                hidegrid: false,
                fixar: true,
                largura: "4rem"
            },
        } : {}),
        acessoinicial: {
            exibir: 'main.grupo.acessoinicial',
            visivel: true,
            tipo: 'boolean',
            template: 'acessoinicial.template',
            hidegrid: false,
             fixar: true,
            largura: "4rem"
        },
    };
}

// ─── Renderers customizados ───────────────────────────────────────────────────

const booleanTemplate = (campo) => (rowData) => (
    <i className={`pi ${rowData[campo] ? 'pi-check text-green-500' : 'pi-times text-red-400'}`}></i>
);

const renderers = {
    acessototal:   { template: booleanTemplate('acessototal') },
    acessoinicial: { template: booleanTemplate('acessoinicial') },
};

// ─── Formulário de campos (render prop) ───────────────────────────────────────

class GrupoFormulario extends Component {
    render() {
        const { model, contexto } = this.props;
        const larguraAcessoInicial = model.acessototal ? 'md:col-3' : 'md:col-6';
        return (
            <>
                <div className="field col-12 md:col-6 mt-3">
                    <ModelTextInput model={model.nome} contexto={contexto} />
                </div>
                {model.acessototal && (
                    <div className="field col-12 md:col-3 mt-3">
                        <ModelBoolean model={model.acessototal} contexto={contexto} />
                    </div>
                )}
                <div className={`field col-12 ${larguraAcessoInicial} mt-3`}>
                    <ModelBoolean model={model.acessoinicial} contexto={contexto} />
                </div>
                <div className="field col-12 mt-3">
                    <ModelTextInput model={model.descricao} contexto={contexto} />
                </div>
            </>
        );
    }
}

// ─── Template (componente exportado) ─────────────────────────────────────────

class GrupoTemplate extends Component {

    processos() {
        const { permissoes } = this.props;
        const t = (chave) => UtilService.traduzir(chave);

        const podeSincronizar = permissoes?.grupo?.processos?.some(
            e => e.state === 'main.grupo.sincronizarpermissoes' && e.permitido
        );

        const lista = [];

        lista.push({
            label: 'main.duplicar',
            icon: 'pi pi-clone',
            disabled: true,
            ativo: (processo, selected) => {
                processo.disabled = selected.length !== 1;
                return processo;
            },
            command: () => { this.gridRef?.duplicar(); }
        });

        if (podeSincronizar) {
            lista.push({
                label: 'main.grupo.sincronizarpermissoes',
                icon: 'pi pi-sync',
                command: () => {
                    confirmDialog({
                        header: t('main.grupo.sincronizarpermissoes.header'),
                        message: t('main.grupo.sincronizarpermissoes.mensagem'),
                        icon: 'pi pi-exclamation-triangle',
                        className: 'w-4',
                        draggable: false,
                        defaultFocus: 'reject',
                        acceptClassName: 'p-button-warning',
                        accept: async () => {
                            try {
                                await Grupo.sincronizarPermissoes();
                                UtilService.showToast({ severity: 'success', summary: t('main.grupo.sincronizarpermissoes.sucesso') });
                            } catch {
                                UtilService.showToast({ severity: 'error', summary: t('main.grupo.sincronizarpermissoes.erro') });
                            }
                        },
                    });
                },
            });
        }

        return lista;
    }

    render() {
        const model = buildModel(!!this.props.permissoes?.permiteacessogeral);
        return (
            <EntityGrid
                ref={(ref) => this.gridRef = ref}
                entidade={Grupo}
                model={model}
                identidade="main.grupo"
                permissoes={this.props.permissoes}
                icone="pi-tags"
                storageKey="dt-grupos"
                processos={this.processos()}
                getRelacionados={(selected) => buildRelacionados(
                    'grupo',
                    this.props.permissoes,
                    this.props.navigate,
                    () => selected?.[0],
                )}
                renderers={renderers}
                configuracao={this.props.configuracao}
                navigate={this.props.navigate}
                location={this.props.location}
                searchparams={this.props.searchparams}
                formulario={(m, ctx) => <GrupoFormulario model={m} contexto={ctx} />}
            />
        );
    }
}

export default GrupoTemplate;
